import React, { useRef } from 'react'
import { cn } from '../lib/cn'

const tabListVariants = {
    segmented: 'inline-flex min-w-max items-center gap-1 rounded-lg border border-sgez-border bg-sgez-surface-sunken p-1',
    underline: 'flex w-max min-w-full items-center gap-1 border-b border-sgez-border',
}

const tabVariants = {
    segmented: {
        base: 'rounded-md border border-transparent px-3 py-2',
        active: 'border-sgez-border bg-sgez-surface text-sgez-primary shadow-sm',
        inactive: 'text-sgez-muted hover:bg-sgez-surface-subtle hover:text-sgez-ink',
    },
    underline: {
        base: '-mb-px border-b-2 border-transparent px-3 py-2.5',
        active: 'border-sgez-primary bg-sgez-surface-subtle text-sgez-primary',
        inactive: 'text-sgez-muted hover:border-sgez-border-strong hover:bg-sgez-surface-subtle hover:text-sgez-ink',
    },
}

export function PageTabs({
    items = [],
    activeId,
    onChange,
    variant = 'segmented',
    ariaLabel = '페이지 탭',
    className,
}) {
    const tabRefs = useRef([])
    const resolvedVariant = tabVariants[variant] ? variant : 'segmented'
    const enabledIndexes = items.reduce((indexes, item, index) => {
        if (!item.disabled) indexes.push(index)
        return indexes
    }, [])
    const activeIndex = items.findIndex((item) => item.id === activeId && !item.disabled)
    const focusableIndex = activeIndex >= 0 ? activeIndex : enabledIndexes[0]

    const activateTab = (index) => {
        const item = items[index]
        if (!item || item.disabled) return

        tabRefs.current[index]?.focus()
        onChange?.(item.id)
    }

    const handleKeyDown = (event, currentIndex) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
        if (enabledIndexes.length === 0) return

        event.preventDefault()

        if (event.key === 'Home') {
            activateTab(enabledIndexes[0])
            return
        }

        if (event.key === 'End') {
            activateTab(enabledIndexes[enabledIndexes.length - 1])
            return
        }

        const enabledPosition = enabledIndexes.indexOf(currentIndex)
        const direction = event.key === 'ArrowRight' ? 1 : -1
        const nextPosition = (enabledPosition + direction + enabledIndexes.length) % enabledIndexes.length
        activateTab(enabledIndexes[nextPosition])
    }

    return (
        <div className={cn('w-full overflow-x-auto', className)}>
            <div
                role="tablist"
                aria-label={ariaLabel}
                aria-orientation="horizontal"
                className={tabListVariants[resolvedVariant]}
            >
                {items.map((item, index) => {
                    const isActive = item.id === activeId
                    const styles = tabVariants[resolvedVariant]

                    return (
                        <button
                            key={item.id}
                            ref={(element) => {
                                tabRefs.current[index] = element
                            }}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            aria-controls={item.panelId}
                            aria-label={item.ariaLabel}
                            disabled={item.disabled}
                            tabIndex={index === focusableIndex ? 0 : -1}
                            className={cn(
                                'shrink-0 whitespace-nowrap text-sm font-medium transition-colors outline-none',
                                'focus-visible:ring-2 focus-visible:ring-sgez-primary focus-visible:ring-offset-2 focus-visible:ring-offset-sgez-background',
                                'disabled:cursor-not-allowed disabled:opacity-50',
                                styles.base,
                                isActive ? styles.active : styles.inactive,
                                item.className
                            )}
                            onClick={() => onChange?.(item.id)}
                            onKeyDown={(event) => handleKeyDown(event, index)}
                        >
                            {item.label}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}
